import React, { useRef } from "react";
import { Field } from "react-final-form";
import { styled } from "@mui/material/styles";
import classNames from "classnames";
import FormHelperText from "@mui/material/FormHelperText";
import { BannerPlaceholder } from "./components/BannerPlaceholder";
import { BannerPreview } from "./components/BannerPreview";
import { BannerCropper } from "./components/BannerCropper";
import { BannerControls } from "./components/BannerControls";
import { BannerToggle } from "./components/BannerToggle";
import { useBannerLogic } from "./hooks/useBannerLogic";
import type { BannerValue, Orientation } from "./hooks/useBannerLogic";

const PREFIX = "BannerField";

const classes = {
  root: `${PREFIX}-root`,
  hover: `${PREFIX}-hover`,
  input: `${PREFIX}-input`,
};

const Root = styled("div")(({ theme }) => ({
  position: "relative",
  [`&.${classes.root}`]: {
    overflow: "hidden",
    ...theme.applyStyles("dark", {
      border: "1px solid rgba(255, 255, 255, 0.23)",
    }),
    ...theme.applyStyles("light", {
      border: "1px solid rgba(0, 0, 0, 0.23)",
    }),
    borderRadius: 4,
    width: "100%",
    paddingTop: "56.25%",
    height: 0,
    position: "relative",
    "&:hover": {
      ...theme.applyStyles("dark", {
        borderColor: "rgba(255, 255, 255, 1)",
      }),
      ...theme.applyStyles("light", {
        borderColor: "rgba(0, 0, 0, 1)",
      }),
    },
  },

  [`&.${classes.hover}`]: {
    ...theme.applyStyles("dark", {
      borderColor: "rgba(255, 255, 255, 1)",
    }),
    ...theme.applyStyles("light", {
      borderColor: "rgba(0, 0, 0, 1)",
    }),
  },

  [`& .${classes.input}`]: {
    position: "absolute",
    opacity: 0,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
  },
}));

interface BannerFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  helperText?: string;
  accept?: string;
  successMessage?: string;
  maxSize?: number;
  disabled?: boolean;
  orientation?: Orientation;
  /** Слот, открытый первым, пока картинок нет (см. BannerFieldDefinition) */
  defaultOrientation?: Orientation;
}

interface BannerFieldInnerProps extends Omit<BannerFieldProps, "name"> {
  value: BannerValue;
  onChange: (value: BannerValue) => void;
}

function BannerFieldInner({
  value,
  onChange,
  label,
  required = false,
  accept = "image/*",
  successMessage,
  maxSize,
  disabled,
  orientation,
  defaultOrientation,
}: BannerFieldInnerProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    activeImageType,
    setActiveImageType,
    hover,
    currentImage,
    currentCroppedImage,
    currentOrientation,
    aspect,
    crop,
    zoom,
    createPreviewResult,
    currentValue,
    hasImage,
    isEmpty,
    isCropping,
    horizontalImage,
    verticalImage,
    handleFileChange,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    onClickDelete,
    onCropChange,
    onCropComplete,
    onZoomChange,
    onClickCropSave,
    onClickCrop,
  } = useBannerLogic({
    value,
    onChange,
    orientation,
    defaultOrientation,
    successMessage,
    maxSize,
  });

  const onClickUpload = () => {
    inputRef.current?.click();
  };

  const onChangeFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFileChange(file);
    event.target.value = "";
  };

  // Переключатель 16:9 / 2:3 виден и на пустом поле: иначе нельзя выбрать
  // портрет до первой загрузки, и для актёров заливали 16:9 по ошибке.
  const showToggle = !orientation;

  return (
    <Root
      className={classNames(classes.root, { [classes.hover]: hover })}
      style={{ paddingTop: "56.25%" }}
      onDragEnter={disabled ? undefined : onDragEnter}
      onDragLeave={disabled ? undefined : onDragLeave}
      onDragOver={disabled ? undefined : onDragOver}
      onDrop={disabled ? undefined : onDrop}
    >
      {showToggle && (
        <BannerToggle
          activeImageType={activeImageType}
          onImageTypeChange={setActiveImageType}
          hasHorizontalImage={Boolean(horizontalImage?.value)}
          hasVerticalImage={Boolean(verticalImage?.value)}
        />
      )}

      {isEmpty && (
        <BannerPlaceholder
          orientation={currentOrientation}
          label={label}
          required={required}
          onUploadClick={onClickUpload}
          isLoading={createPreviewResult.loading}
        />
      )}

      {hasImage && !isCropping && currentImage && (
        <BannerPreview
          orientation={currentOrientation}
          imageUrl={currentImage}
          croppedImageUrl={currentCroppedImage}
          isLoading={createPreviewResult.loading}
        />
      )}

      {isCropping && currentImage && (
        <BannerCropper
          image={currentImage}
          crop={crop}
          zoom={zoom}
          aspect={aspect}
          onCropChange={onCropChange}
          onCropComplete={onCropComplete}
          onZoomChange={onZoomChange}
        />
      )}

      {hasImage && (
        <BannerControls
          isCropping={isCropping}
          onDelete={onClickDelete}
          onCrop={onClickCrop}
          onCropSave={onClickCropSave}
        />
      )}

      <input
        accept={accept}
        type="file"
        className={classes.input}
        required={required && !currentValue}
        ref={inputRef}
        onChange={onChangeFileInput}
        disabled={disabled}
      />
    </Root>
  );
}

export function BannerField({
  name,
  label,
  required = false,
  helperText,
  accept = "image/*",
  successMessage,
  maxSize,
  disabled,
  orientation,
  defaultOrientation,
}: BannerFieldProps) {
  return (
    <>
      <Field<BannerValue>
        name={name}
        format={orientation ? getBannerFormat(orientation) : undefined}
        parse={orientation ? getBannerParse(orientation) : undefined}
      >
        {({ input: { onChange, value } }) => (
          <BannerFieldInner
            value={value}
            onChange={onChange}
            label={label}
            required={required}
            accept={accept}
            successMessage={successMessage}
            maxSize={maxSize}
            disabled={disabled}
            orientation={orientation}
            defaultOrientation={defaultOrientation}
          />
        )}
      </Field>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </>
  );
}

/** Форма хранит один id; в компонент отдаём объект слота */
function getBannerFormat(orientation: Orientation) {
  return (value: string | null | undefined): BannerValue =>
    orientation === "horizontal"
      ? { horizontalId: value }
      : { verticalId: value };
}

/** Компонент отдаёт объект; в форму пишем один id */
function getBannerParse(orientation: Orientation) {
  return (value: BannerValue): string | null | undefined =>
    orientation === "horizontal" ? value.horizontalId : value.verticalId;
}
